import { Component } from 'react'
import { PiCaretDown, PiList, PiMagnifyingGlass, PiSidebarSimple, PiStar, PiStarFill } from 'react-icons/pi'
import { Api, LocalStorage } from '../../Services'
import i18n from '../../i18n'
import swaller from '../../Library/Tools/Swaller'
import { getModuleCategory } from '../../Data/enums/homeModuleCategories'
import { getSezioneIcon, HIDDEN_FROM_GRID } from '../../Utils/sezioniDisplay'
import { buildSidebarGroups, toggleClosedGroup, FAVORITES_KEY } from './sidebarGroups'
import {
  EXPANDED_WIDTH,
  COLLAPSED_WIDTH,
  SidebarContainer,
  SidebarOverlay,
  SidebarHeader,
  SearchBox,
  SearchInput,
  PanelToggle,
  SidebarList,
  EmptySearch,
  Group,
  GroupHeader,
  GroupTitle,
  GroupChevron,
  GroupBody,
  ItemRow,
  Item,
  ItemLabel,
  FavStar,
  NewBadge,
  Tooltip,
  MobileTrigger
} from './Sidebar.styles'

const PUBLIC_PATHS = ['/', '/login', '/firstlogin', '/firstotp', '/resetpassword']
const CLOSED_GROUPS_KEY = 'sidebarClosedGroups'

const loadClosedGroups = () => {
  const saved = LocalStorage.get(CLOSED_GROUPS_KEY)
  return Array.isArray(saved) ? saved : []
}

export default class Sidebar extends Component {
  constructor (props) {
    super(props)
    this.state = {
      sezioni: [],
      favorites: [],
      searchTerm: '',
      newPreventiviCount: 0,
      loadedUserId: null,
      collapsed: LocalStorage.get('sidebarCollapsed') === true,
      mobileOpen: false,
      closedGroups: loadClosedGroups(),
      hoverTip: null,
      isMobile: !!window.matchMedia?.('(max-width: 768px)').matches
    }
    this.preventiviEpoch = 0
    this.pendingFavorites = new Set()
    this.mobileQuery = window.matchMedia?.('(max-width: 768px)')
  }

  componentDidMount () {
    this._mounted = true
    this.mobileQuery?.addEventListener('change', this.handleMobileChange)
    this.loadSezioni()
    this.syncSidebarWidthVar()
  }

  handleMobileChange = (e) => this.setState({ isMobile: e.matches })

  componentDidUpdate (prevProps) {
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      this.setState({ mobileOpen: false, searchTerm: '', hoverTip: null })
      if (this.isOnPreventivi()) this.markPreventiviSeen()
      this.openActiveGroup()
    }
    const currentUserId = LocalStorage.get('user')?._id
    if (!currentUserId && this.state.loadedUserId) this.resetUser()
    else if (currentUserId !== this.state.loadedUserId) this.loadSezioni()
    this.syncSidebarWidthVar()
  }

  componentWillUnmount () {
    this._mounted = false
    this.mobileQuery?.removeEventListener('change', this.handleMobileChange)
    clearInterval(this.preventiviInterval)
  }

  async loadSezioni () {
    const cachedUser = LocalStorage.get('user')
    if (!cachedUser) return
    try {
      // Fetch utente fresco (non dalla cache LocalStorage) cosi' le sezioni riflettono
      // permessi aggiornati subito
      const [userResponse, sezioniResponse, preferencesResponse] = await Promise.all([
        Api.get(`/users/${cachedUser._id}`),
        Api.get('/sezioni'),
        Api.get(`/users/${cachedUser._id}/preferences`)
      ])
      if (!this._mounted || !userResponse.ok || !sezioniResponse.ok) return
      if (LocalStorage.get('user')?._id !== cachedUser._id) return // logout/cambio utente nel frattempo
      const user = userResponse.data
      const sezioni = sezioniResponse.data
        .filter(sezione => user.sezioni.includes(sezione._id) && !HIDDEN_FROM_GRID.includes(sezione.title))
        .sort((a, b) => a.title.localeCompare(b.title))
      const favorites = preferencesResponse.ok ? (preferencesResponse.data?.favoriteSezioni || []) : []
      // Nuovo utente: lastVisit va riletto dal backend, non riusato da quello precedente
      this.resetPreventiviTracking(undefined)
      this.setState({ sezioni, favorites, loadedUserId: cachedUser._id }, () => {
        this.syncSidebarWidthVar()
        this.startPreventiviPolling()
        this.openActiveGroup()
      })
    } catch (error) {
      console.error('Error loading sidebar sezioni:', error)
    }
  }

  syncSidebarWidthVar () {
    const pathname = this.props.location?.pathname || ''
    const hidden = PUBLIC_PATHS.includes(pathname) || this.state.sezioni.length === 0
    const width = hidden ? '0px' : (this.state.collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)
    document.documentElement.style.setProperty('--sidebar-width', width)
  }

  toggleCollapsed = () => {
    this.setState(prev => {
      const collapsed = !prev.collapsed
      LocalStorage.set('sidebarCollapsed', collapsed)
      // Da chiusa il campo ricerca non c'e': un filtro rimasto attivo non si potrebbe svuotare
      return { collapsed, searchTerm: '', hoverTip: null }
    }, () => this.syncSidebarWidthVar())
  }

  handlePanelToggle = () => {
    if (this.state.isMobile) this.setState({ mobileOpen: false })
    else this.toggleCollapsed()
  }

  toggleGroup = (key) => {
    // Da contratta i gruppi sono solo divisori; con ricerca attiva sono forzati aperti:
    // un clic salverebbe uno stato che l'utente non vede
    if (this.isCollapsed() || this.state.searchTerm.trim()) return
    this.setState(prev => {
      const closedGroups = toggleClosedGroup(prev.closedGroups, key)
      LocalStorage.set(CLOSED_GROUPS_KEY, closedGroups)
      return { closedGroups }
    })
  }

  // Entrando in una sezione il suo gruppo si riapre: niente voce attiva nascosta in un gruppo chiuso
  openActiveGroup () {
    const active = this.state.sezioni.find(s => this.isActive(s))
    if (!active) return
    const key = getModuleCategory(active.title)
    if (!this.state.closedGroups.includes(key)) return
    const closedGroups = this.state.closedGroups.filter(k => k !== key)
    LocalStorage.set(CLOSED_GROUPS_KEY, closedGroups)
    this.setState({ closedGroups })
  }

  showTip = (e, label) => {
    if (!this.isCollapsed()) return
    const rect = e.currentTarget.getBoundingClientRect()
    this.setState({ hoverTip: { label, top: rect.top + rect.height / 2 } })
  }

  hideTip = () => {
    if (this.state.hoverTip) this.setState({ hoverTip: null })
  }

  toggleFavorite = async (e, sezioneId) => {
    e.stopPropagation()
    if (this.pendingFavorites.has(sezioneId)) return // doppio clic: una richiesta alla volta
    this.pendingFavorites.add(sezioneId)
    const isFavorite = this.state.favorites.includes(sezioneId)
    const response = await Api.patch(`/users/${this.state.loadedUserId}/preferences/favorite`, {
      sezioneId,
      action: isFavorite ? 'remove' : 'add'
    })
    this.pendingFavorites.delete(sezioneId)
    if (!this._mounted) return
    if (!response.ok) {
      swaller.error(i18n.t('errorFavorites', { ns: 'home' }))
      return
    }
    this.setState(prev => ({
      favorites: isFavorite ? prev.favorites.filter(id => id !== sezioneId) : [...prev.favorites, sezioneId]
    }))
  }

  // Logout via navigate (niente reload): la Sidebar resta montata con i dati dell'utente precedente
  resetUser () {
    clearInterval(this.preventiviInterval)
    this.preventiviInterval = null
    this.resetPreventiviTracking(undefined)
    this.setState({ sezioni: [], favorites: [], newPreventiviCount: 0, loadedUserId: null })
  }

  // Cambio utente o visita a Preventivi: i fetch in volo (epoch vecchio) vengono scartati
  resetPreventiviTracking (lastVisit) {
    this.preventiviEpoch += 1
    this.lastVisitPreventivi = lastVisit
  }

  // Su mobile la sidebar e' un drawer a 220px: lo stato "chiusa" del desktop non vale
  isCollapsed () {
    return this.state.collapsed && !this.state.isMobile
  }

  isOnPreventivi () {
    const sezione = this.state.sezioni.find(s => s.title === 'Preventivi')
    return !!sezione && this.isActive(sezione)
  }

  // La pagina Preventivi registra la visita sul backend al mount: qui basta allinearsi in locale
  markPreventiviSeen () {
    this.resetPreventiviTracking(new Date().toISOString())
    // Ingresso diretto su sotto-pagine (es. /preventivi/modifica): la lista non monta, quindi registra qui
    Api.post('/users/me/visit-preventivi')
    if (this.state.newPreventiviCount !== 0) this.setState({ newPreventiviCount: 0 })
  }

  startPreventiviPolling () {
    clearInterval(this.preventiviInterval)
    this.preventiviInterval = null
    if (!this.state.sezioni.some(s => s.title === 'Preventivi')) {
      this.setState({ newPreventiviCount: 0 })
      return
    }
    this.fetchNewPreventiviCount()
    this.preventiviInterval = setInterval(this.fetchNewPreventiviCount, 60000)
  }

  fetchNewPreventiviCount = async () => {
    if (!LocalStorage.get('user')) return // logout: la Sidebar resta montata
    if (this.isOnPreventivi()) return this.markPreventiviSeen()
    const epoch = this.preventiviEpoch

    if (this.lastVisitPreventivi === undefined) {
      const userResponse = await Api.get('/users/me')
      if (!this._mounted || !userResponse.ok || epoch !== this.preventiviEpoch) return
      this.lastVisitPreventivi = userResponse.data?.preferences?.lastVisitPreventivi || null
    }

    const params = this.lastVisitPreventivi ? `?after=${new Date(this.lastVisitPreventivi).toISOString()}` : ''
    const response = await Api.get(`/preventivi/count-new${params}`)
    if (!this._mounted || !response.ok || epoch !== this.preventiviEpoch) return
    // Nel frattempo l'utente puo' essere entrato in Preventivi: il conteggio e' gia' vecchio
    if (this.isOnPreventivi()) return this.markPreventiviSeen()
    this.setState({ newPreventiviCount: response.data?.count || 0 })
  }

  handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') this.setState({ searchTerm: '' })
  }

  translateTitle = (title) => i18n.t(`titles.${title}`, { ns: 'home', defaultValue: title })

  isActive (sezione) {
    const pathname = this.props.location?.pathname || ''
    return pathname === sezione.path || pathname.startsWith(`${sezione.path}/`)
  }

  renderIcon (sezione) {
    const IconComponent = getSezioneIcon(sezione)
    return <IconComponent />
  }

  renderItem (sezione, keyPrefix) {
    const { favorites, newPreventiviCount } = this.state
    const collapsed = this.isCollapsed()
    const label = this.translateTitle(sezione.title)
    const isFavorite = favorites.includes(sezione._id)
    const favLabel = i18n.t(isFavorite ? 'removeFavorite' : 'addFavorite', { ns: 'home' })
    const showBadge = sezione.title === 'Preventivi' && newPreventiviCount > 0 && !this.isActive(sezione)
    const itemTitle = showBadge
      ? `${label} · ${i18n.t('newPreventivi', { ns: 'navbar', count: newPreventiviCount })}`
      : label

    return (
      <ItemRow key={`${keyPrefix}-${sezione._id}`}>
        <Item
          type='button'
          $active={this.isActive(sezione)}
          aria-current={this.isActive(sezione) ? 'page' : undefined}
          title={collapsed ? undefined : itemTitle}
          onClick={() => this.props.navigate(sezione.path)}
          onMouseEnter={(e) => this.showTip(e, itemTitle)}
          onMouseLeave={this.hideTip}
          onFocus={(e) => this.showTip(e, itemTitle)}
          onBlur={this.hideTip}
        >
          {this.renderIcon(sezione)}
          <ItemLabel $collapsed={collapsed}>{label}</ItemLabel>
          {showBadge && <NewBadge $collapsed={collapsed}>{collapsed ? null : newPreventiviCount}</NewBadge>}
        </Item>
        {!collapsed && (
          <FavStar
            type='button'
            className='fav-star'
            $isFavorite={isFavorite}
            aria-label={favLabel}
            title={favLabel}
            onClick={(e) => this.toggleFavorite(e, sezione._id)}
          >
            {isFavorite ? <PiStarFill /> : <PiStar />}
          </FavStar>
        )}
      </ItemRow>
    )
  }

  renderGroup ({ key, label, items, open }) {
    const collapsed = this.isCollapsed()
    const shown = collapsed || open // da contratta il rail mostra tutte le icone
    const locked = collapsed || this.state.searchTerm.trim() !== ''
    return (
      <Group key={key}>
        <GroupHeader
          type='button'
          $collapsed={collapsed}
          $locked={locked}
          aria-expanded={shown}
          tabIndex={collapsed ? -1 : 0}
          onClick={() => this.toggleGroup(key)}
        >
          <GroupTitle $collapsed={collapsed}>{label}</GroupTitle>
          <GroupChevron $collapsed={collapsed} $open={shown}><PiCaretDown /></GroupChevron>
        </GroupHeader>
        <GroupBody $open={shown}>
          <div>{items.map(sezione => this.renderItem(sezione, key))}</div>
        </GroupBody>
      </Group>
    )
  }

  render () {
    const { sezioni, favorites, searchTerm, mobileOpen, closedGroups, hoverTip, isMobile } = this.state
    const collapsed = this.isCollapsed()
    const pathname = this.props.location?.pathname || ''

    if (PUBLIC_PATHS.includes(pathname) || sezioni.length === 0) return null

    const { favoriteItems, favoritesOpen, groups } = buildSidebarGroups(sezioni, {
      favorites,
      term: searchTerm,
      translate: this.translateTitle,
      closedGroups
    })
    const searchPlaceholder = i18n.t('searchPlaceholder', { ns: 'home' })
    const panelLabel = i18n.t(collapsed ? 'expandSidebar' : 'collapseSidebar', { ns: 'navbar' })

    return (
      <>
        <SidebarOverlay $visible={mobileOpen} onClick={() => this.setState({ mobileOpen: false })} />
        <MobileTrigger
          $hidden={mobileOpen}
          onClick={() => this.setState(prev => ({ mobileOpen: !prev.mobileOpen }))}
          title={i18n.t('openSidebar', { ns: 'navbar' })}
          aria-label={i18n.t('openSidebar', { ns: 'navbar' })}
        >
          <PiList />
        </MobileTrigger>
        <SidebarContainer $collapsed={collapsed} $mobileOpen={mobileOpen}>
          <SidebarHeader>
            <SearchBox $collapsed={collapsed} aria-hidden={collapsed}>
              <PiMagnifyingGlass />
              <SearchInput
                type='search'
                value={searchTerm}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                tabIndex={collapsed ? -1 : 0}
                onChange={(e) => this.setState({ searchTerm: e.target.value })}
                onKeyDown={this.handleSearchKeyDown}
              />
            </SearchBox>
            <PanelToggle
              type='button'
              onClick={this.handlePanelToggle}
              aria-label={panelLabel}
              title={panelLabel}
              aria-expanded={isMobile ? mobileOpen : !collapsed}
            >
              <PiSidebarSimple />
            </PanelToggle>
          </SidebarHeader>
          <SidebarList onScroll={this.hideTip}>
            {favoriteItems.length > 0 && this.renderGroup({
              key: FAVORITES_KEY,
              label: i18n.t('favorites', { ns: 'home' }),
              items: favoriteItems,
              open: favoritesOpen
            })}
            {groups.map(({ category, items, open }) => this.renderGroup({
              key: category.key,
              label: i18n.t(category.labelKey, { ns: 'home' }),
              items,
              open
            }))}
            {favoriteItems.length === 0 && groups.length === 0 && (
              <EmptySearch>{i18n.t('noResults', { ns: 'home', term: searchTerm.trim() })}</EmptySearch>
            )}
          </SidebarList>
        </SidebarContainer>
        {hoverTip && <Tooltip style={{ top: hoverTip.top }}>{hoverTip.label}</Tooltip>}
      </>
    )
  }
}
